import axios, { HttpStatusCode } from 'axios'
import { Platform } from 'quasar'
import * as storage from 'src/plugins/helpers/storage'
import { SHARED_AUTH_COOKIE } from 'src/constants/shared-auth-cookie'
import Vue from 'vue'
import { detectWidgetPlatform } from 'src/utils/widget-platform-detector'
import { getAppUrl } from 'src/plugins/mixins/classic.mixin'
import { isElectron } from 'src/plugins/helpers/electron'

// Flag to prevent request storms after session expiry.
// Once set, all outgoing requests are aborted immediately.
let sessionExpired = false

// Threshold in milliseconds for considering a request as slow
// Requests taking longer than this will be reported to Sentry
const SLOW_REQUEST_THRESHOLD_MS = 10000

// Twilio event-bridge (TaskRouter JS SDK) responds with Access-Control-Allow-Origin: *
// which is not allowed when credentials are included. Patch XHR/fetch so Twilio requests
// do not send credentials.
const NativeXHR = window.XMLHttpRequest
const TWILIO_HOST = 'event-bridge.twilio.com'

window.XMLHttpRequest = function () {
  const xhr = new NativeXHR()
  const origOpen = xhr.open.bind(xhr)
  const origSetRequestHeader = xhr.setRequestHeader.bind(xhr)

  xhr.open = function (method, url, ...rest) {
    xhr._isTwilio = typeof url === 'string' && url.includes(TWILIO_HOST)
    return origOpen(method, url, ...rest)
  }

  // Block disallowed headers for Twilio requests
  const BLOCKED_HEADERS = ['requested-from', 'x-requested-with', 'widget-platform', 'authorization', 'version']
  xhr.setRequestHeader = function (name, value) {
    if (xhr._isTwilio && BLOCKED_HEADERS.includes(name.toLowerCase())) return
    return origSetRequestHeader(name, value)
  }

  return xhr
}

window.axios = axios

window.axios.defaults.baseURL = process.env.API_URL
// Do not set withCredentials globally so third-party (e.g. Twilio) requests are not affected.
// We set it per-request for our API only in the request interceptor below.

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

// Client version, sent on every request regardless of platform. Electron refreshes this
// later from the installed app version once the main-process IPC round-trip resolves.
if (process.env.VERSION) {
  window.axios.defaults.headers.common['Version'] = process.env.VERSION
}

if (Platform.is.desktop) {
  window.axios.defaults.headers.common['Requested-From'] = 'talk2-web'
}

// Intentional overwrite: desktop-Electron wins over not-a-phone.
if (isElectron()) {
  window.axios.defaults.headers.common['Requested-From'] = 'talk2-desktop'
}

if (Platform.within.iframe) {
  window.axios.defaults.headers.common['Requested-From'] = 'talk2-iframe'

  // Try to detect widget platform and add it to headers
  try {
    const platform = detectWidgetPlatform()
    if (platform) {
      window.axios.defaults.headers.common['Widget-Platform'] = platform
    }
  } catch (e) {
    console.warn('Could not detect widget platform for axios header', e)
  }
}

if (storage.local.getItem('api_token')) {
  window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + storage.local.getItem('api_token')
}

// Block all requests once session has expired — page is about to redirect
window.axios.interceptors.request.use(config => {
  if (sessionExpired) {
    return Promise.reject(new axios.Cancel('Session expired'))
  }
  return config
})

// Add request interceptor: send credentials only for our API; strip custom headers for Twilio; track request start time
window.axios.interceptors.request.use(config => {
  const url = config.url || ''
  const hostname = window.location.hostname
  let isOurApi
  if (!hostname) {
    // Electron file:// context — window.location.hostname is empty, fall back to URL prefix match
    const baseURL = window.axios.defaults.baseURL || ''
    const reportingURL = process.env.API_REPORTING_URL || ''
    const resolvedUrl = url.startsWith('http') ? url : (baseURL + url)
    isOurApi = (baseURL && resolvedUrl.startsWith(baseURL)) || (reportingURL && resolvedUrl.startsWith(reportingURL))
  } else {
    let targetHost = hostname
    if (url.startsWith('http')) {
      try {
        targetHost = new URL(url).hostname
      } catch (e) {
        targetHost = ''
      }
    }
    const parentDomain = getParentDomain(hostname)
    isOurApi = targetHost && parentDomain && (targetHost === parentDomain || targetHost.endsWith('.' + parentDomain))
  }
  config.withCredentials = !!isOurApi
  config.metadata = { startTime: new Date().getTime() }

  return config
})

function getParentDomain (hostname) {
  return hostname.substring(hostname.lastIndexOf('.', hostname.lastIndexOf('.') - 1) + 1)
}

// Clear all client-side auth state so nothing can re-authenticate.
// Shared by every 401 path that forces a logout (session expiry, force logout,
// password expiry).
function clearClientAuthState () {
  storage.local.removeItem('api_token')
  storage.local.removeItem('company_id')
  storage.local.removeItem('current_user_id')
  storage.local.removeItem('shared_cookie')
  delete window.axios.defaults.headers.common['Authorization']

  // Clear auth cookies from the browser
  const parts = window.location.hostname.split('.')
  if (parts.length > 2) parts.shift()
  const domain = '.' + parts.join('.')
  document.cookie = SHARED_AUTH_COOKIE + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + domain
}

// Add response interceptor to check for slow requests
window.axios.interceptors.response.use(response => {
  // Check for slow requests in success cases
  if (response?.config?.metadata?.startTime) {
    const endTime = new Date().getTime()
    const duration = endTime - response.config.metadata.startTime

    if (duration > SLOW_REQUEST_THRESHOLD_MS && window.Sentry) {
      window.Sentry.captureMessage('Slow request detected', {
        level: 'warning',
        extra: {
          url: response?.config?.url,
          method: response?.config?.method,
          duration: duration,
          status: response?.status
        }
      })
    }
  }

  return response
}, error => {
  if (axios.isCancel(error)) {
    // Swallow session-expiry cancels only — page is redirecting, suppress all pending request handlers
    // Component-level cancels (e.g. CancelToken) must propagate so .finally() cleanup runs
    if (error.message === 'Session expired') {
      return new Promise(() => {})
    }
    return Promise.reject(error)
  }

  if (error.response) {
    switch (error.response.status) {
      case 401:
        if (error.response?.data?.password_expired && !sessionExpired) {
          sessionExpired = true

          clearClientAuthState()

          // Route to the password-reset flow instead of leaving the app hung on the
          // loading screen with a bare 401. A reset email was already sent server-side.
          const resetQuery = new URLSearchParams({
            email: error.response.data.email || '',
            sent: 'true',
            message: error.response.data.error || ''
          }).toString()

          setTimeout(() => {
            if (isElectron()) {
              // file:// context can't deep-link to a route; reload back to login,
              // where a re-login attempt re-triggers the reset flow.
              window.location.reload()
            } else {
              window.location.href = window.location.origin + '/forgot-password?' + resetQuery
            }
          }, 0)

          return new Promise(() => {})
        } else if ((error.response?.data?.session_expired || error.response?.data?.force_logout) && !sessionExpired) {
          sessionExpired = true

          // Read company_id BEFORE clearing localStorage
          const impersonatedCompanyId = storage.local.getItem('company_id')
          const isAdminImpersonation = error.response.data.is_admin_impersonation

          clearClientAuthState()

          // Redirect through api-core to clear its session too
          let returnPath = window.location.origin
          if (isAdminImpersonation && impersonatedCompanyId) {
            returnPath = '/admin/company/' + impersonatedCompanyId + '?tab=company-user-list'
          } else if (isAdminImpersonation) {
            returnPath = '/admin'
          }

          setTimeout(() => {
            if (isElectron()) {
              window.location.reload()
            } else {
              const appUrl = getAppUrl()
              window.location.href = appUrl + '?from_talk_2=1&logout=1&return=' + encodeURIComponent(returnPath)
            }
          }, 0)

          return new Promise(() => {})
        } else if (!sessionExpired) {
          console.log('Session expired or user not authenticated', error.response?.data)
        }
        break
      // 404
      case HttpStatusCode.NotFound:
        console.log('Requested resource not found', error.response?.data)
        break
      // 422
      case HttpStatusCode.UnprocessableContent:
        console.log('Validation error', error.response?.data)
        break
      // 500
      case HttpStatusCode.InternalServerError:
        console.log('Internal server error', error.response?.data)
        break
      default:
        console.log('An unexpected error occurred', error.response?.data)
    }
  } else if (error.request) {
    console.log('No response received')
  } else {
    console.log('Error setting up the request')
  }

  return Promise.reject(error)
})

Vue.prototype.$axios = window.axios
